import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { DoorOpen, RefreshCw, Trash2, Settings, Zap } from "lucide-react";
import { toast } from "sonner";
import { DoorControllerCard } from "../DoorControllerCard";

export function AdminDoorsTab({ buildings = [] }) {
  const [controllers, setControllers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [toUnlock, setToUnlock] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/door-controllers");
      setControllers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load door controllers");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function doUnlock() {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/platform/door-controllers/${toUnlock.building_id}/unlock`);
      const status = data?.delivery?.status;
      const detail = data?.delivery?.detail || "";
      if (status === "sent" || status === "acked") toast.success(`Door unlocked · ${detail}`);
      else if (status === "pending") toast.info("Queued for gateway");
      else toast.warning(`Unlock result: ${status} · ${detail}`);
      setToUnlock(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally { setUnlocking(false); }
  }

  async function doDelete() {
    try {
      await api.delete(`/platform/door-controllers/${toDelete.building_id}`);
      toast.success("Controller removed");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  const editingLabel = buildings.find((b) => b.id === editingBuilding)?.name || "";
  const editingBuildingObj = buildings.find((b) => b.id === editingBuilding);

  return (
    <div className="space-y-4" data-testid="admin-doors-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <DoorOpen size={14}/> {controllers.filter((c)=>c.configured).length} configured · {controllers.length} building{controllers.length !== 1 ? "s" : ""}
        </div>
        <Button variant="outline" onClick={load} data-testid="doors-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
      </div>

      {controllers.length === 0 ? (
        <div className="card-stroke p-8 text-center text-sm text-muted-foreground" data-testid="doors-empty">
          {loading ? "Loading…" : "No buildings on the platform yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {controllers.map((c) => {
            const b = buildings.find((x) => x.id === c.building_id);
            const configured = c.configured;
            const last = c.last_delivery;
            return (
              <div key={c.building_id} className="card-stroke p-5" data-testid={`door-row-${c.building_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="label-tiny">{b?.name || c.building_name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{b?.code || c.building_code}</div>
                  </div>
                  <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                    configured ? "bg-emerald-500/15 text-emerald-600" : "bg-secondary text-secondary-foreground"
                  }`}>
                    {configured ? c.adapter_type : "not configured"}
                  </span>
                </div>

                {configured && (
                  <div className="mt-4 space-y-2 text-xs">
                    {c.config?.url && (
                      <div className="font-mono text-muted-foreground break-all">URL: {c.config.url}</div>
                    )}
                    {c.config?.has_auth_token && (
                      <div className="font-mono text-muted-foreground">Secret: {c.config.auth_token_preview}</div>
                    )}
                    {last && (
                      <div className="font-mono">
                        Last:&nbsp;
                        <span className={`px-2 py-0.5 rounded-full ${
                          ["sent","acked"].includes(last.status) ? "bg-emerald-500/20 text-emerald-600"
                          : ["failed","expired"].includes(last.status) ? "bg-destructive/15 text-destructive"
                          : "bg-secondary text-secondary-foreground"
                        }`}>{last.status}</span>
                        <span className="text-muted-foreground"> · {last.detail || "—"}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex items-center flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={()=>setEditingBuilding(c.building_id)}
                          data-testid={`door-edit-${c.building_id}`}>
                    <Settings size={14} className="mr-1.5"/>{configured ? "Edit & test" : "Configure"}
                  </Button>
                  <Button size="sm" onClick={()=>setToUnlock(c)}
                          disabled={!configured}
                          data-testid={`door-unlock-${c.building_id}`}>
                    <Zap size={14} className="mr-1.5"/> Unlock door
                  </Button>
                  {configured && (
                    <Button size="sm" variant="ghost" onClick={()=>setToDelete(c)}
                            data-testid={`door-delete-${c.building_id}`}>
                      <Trash2 size={14} className="text-destructive"/>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editingBuilding} onOpenChange={(o)=>{ if (!o) { setEditingBuilding(null); load(); } }}>
        <DialogContent className="max-w-2xl" data-testid="door-edit-modal">
          <DialogHeader>
            <DialogTitle>Door controller · {editingLabel}</DialogTitle>
          </DialogHeader>
          {editingBuilding && (
            <DoorControllerCard buildingId={editingBuilding}
                                buildingLabel={`${editingBuildingObj?.name || ""} (${editingBuildingObj?.code || ""})`} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!toUnlock} onOpenChange={(o)=>{ if (!o) setToUnlock(null); }}>
        <DialogContent data-testid="door-unlock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap size={18} className="text-accent"/> Unlock door remotely?</DialogTitle>
            <DialogDescription>
              This sends an unlock pulse to <span className="font-mono text-foreground">{toUnlock?.building_name || toUnlock?.building_code}</span>'s controller
              and records a <span className="font-mono">door_unlock</span> activity attributed to you. Only do this if you've verified a legitimate reason.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setToUnlock(null)} data-testid="door-unlock-cancel">Cancel</Button>
            <Button onClick={doUnlock} disabled={unlocking} data-testid="door-unlock-confirm">
              {unlocking ? "Unlocking…" : "Unlock now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <DialogContent data-testid="door-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Remove controller for {toDelete?.building_name}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Residents on calls will no longer be able to unlock the door until a new controller is configured.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setToDelete(null)} data-testid="door-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="door-delete-confirm">Remove controller</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
